import { Capacitor } from '@capacitor/core';
import api from './api';

// Web Push subscription management for this browser/device.
// Note: on iOS this only works when the PWA is installed to the home screen (iOS 16.4+).
// The native iOS app (Capacitor) has no Web Push — it uses APNs instead; each exported
// function branches to the native* helpers at the bottom of this file.
const isNative = Capacitor.isNativePlatform();

export function isPushSupported() {
  if (isNative) return true;
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

// Mirrors the unread count onto the app icon (installed PWAs and the iOS app; no-op elsewhere).
export function updateAppBadge(count) {
  if (isNative) {
    // APNs sets the badge when a push arrives; this clears/updates it as notifications are read.
    import('@capawesome/capacitor-badge')
      .then(({ Badge }) => (count > 0 ? Badge.set({ count }) : Badge.clear()))
      .catch(() => {});
    return;
  }
  if (!('setAppBadge' in navigator)) return;
  if (count > 0) {
    navigator.setAppBadge(count).catch(() => {});
  } else {
    navigator.clearAppBadge().catch(() => {});
  }
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
}

// iOS/Safari only shows the permission prompt while a user-gesture activation is live,
// and network awaits consume it. Call this FIRST in the click handler, before any API
// calls; the answer is cached, so the later requestPermission() in subscribeToPush
// resolves instantly. Safe to call unconditionally — no-op if unsupported.
export async function ensurePushPermission() {
  if (isNative) return nativeRequestPermission();
  if (!isPushSupported()) return;
  await Notification.requestPermission();
}

// Asks for permission, subscribes this browser and registers the subscription with the API.
// Throws 'unsupported' | 'no-service-worker' | 'permission-denied' for the caller to translate.
export async function subscribeToPush(vapidPublicKey) {
  if (isNative) return nativeSubscribe();
  if (!isPushSupported()) throw new Error('unsupported');
  const registration = await navigator.serviceWorker.getRegistration();
  if (!registration) throw new Error('no-service-worker');

  // Read the cached state instead of calling requestPermission() again: Safari/iOS
  // returns 'denied' from requestPermission() when called outside a user gesture,
  // even when permission has already been granted via ensurePushPermission().
  let permission = Notification.permission;
  if (permission === 'default') {
    permission = await Notification.requestPermission();
  }
  if (permission !== 'granted') throw new Error('permission-denied');

  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
    });
  }
  const json = subscription.toJSON();
  await api.post('/Notification/push-subscriptions', {
    endpoint: subscription.endpoint,
    p256dh: json.keys.p256dh,
    auth: json.keys.auth,
  });
}

// Removes this browser's subscription both server-side and locally. Best-effort.
export async function unsubscribeFromPush() {
  if (isNative) return nativeUnsubscribe();
  if (!isPushSupported()) return;
  const registration = await navigator.serviceWorker.getRegistration();
  if (!registration) return;
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return;
  await api.post('/Notification/push-subscriptions/delete', { endpoint: subscription.endpoint });
  await subscription.unsubscribe();
}

// Whether the server can deliver push to this device: VAPID for browsers, APNs for the iOS app.
export function isPushConfigured(settings) {
  return isNative ? !!settings.apnsConfigured : !!settings.pushConfigured;
}

// 'granted' | 'denied' | 'default' (not asked yet) — same values as Notification.permission.
export async function getPushPermission() {
  if (!isNative) return Notification.permission;
  const { PushNotifications } = await import('@capacitor/push-notifications');
  const { receive } = await PushNotifications.checkPermissions();
  return receive === 'granted' || receive === 'denied' ? receive : 'default';
}

// ── Native iOS app (APNs) ──
// The plugin is imported lazily so the website bundle never loads it.

const APNS_TOKEN_KEY = 'apnsDeviceToken';
const BUNDLE_ID = `is.${process.env.VUE_APP_BRAND || 'tolspor'}.app`;
const REGISTRATION_TIMEOUT_MS = 15000;

async function nativeRequestPermission() {
  const { PushNotifications } = await import('@capacitor/push-notifications');
  return PushNotifications.requestPermissions();
}

async function nativeSubscribe() {
  const { PushNotifications } = await import('@capacitor/push-notifications');
  let { receive } = await PushNotifications.checkPermissions();
  if (receive !== 'granted' && receive !== 'denied') {
    ({ receive } = await PushNotifications.requestPermissions());
  }
  if (receive !== 'granted') throw new Error('permission-denied');

  // register() answers through events, not its promise; listen first, then register.
  const token = await new Promise((resolve, reject) => {
    const handles = [];
    const finish = (fn, value) => {
      clearTimeout(timer);
      handles.forEach(h => h.remove());
      fn(value);
    };
    const timer = setTimeout(() => finish(reject, new Error('unsupported')), REGISTRATION_TIMEOUT_MS);
    Promise.all([
      PushNotifications.addListener('registration', t => finish(resolve, t.value)),
      PushNotifications.addListener('registrationError', () => finish(reject, new Error('unsupported'))),
    ]).then(h => {
      handles.push(...h);
      return PushNotifications.register();
    }).catch(e => finish(reject, e));
  });

  await api.post('/Notification/apns-tokens', { token, bundleId: BUNDLE_ID });
  localStorage.setItem(APNS_TOKEN_KEY, token);
}

async function nativeUnsubscribe() {
  const token = localStorage.getItem(APNS_TOKEN_KEY);
  if (!token) return;
  await api.post('/Notification/apns-tokens/delete', { token });
  localStorage.removeItem(APNS_TOKEN_KEY);
  const { PushNotifications } = await import('@capacitor/push-notifications');
  await PushNotifications.unregister();
}
